import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FALL_CRAWL_EVENT_TIME_ZONE,
  formatLocalLiveUpdatedAt,
  getFallCrawlCountdownParts,
  getFallCrawlEventHoursText,
  getFallCrawlEventTimeline,
  getFallCrawlScheduleEvent,
  getLocalLiveDirectionsUrl,
  getLocalLiveEffectiveStopStatus,
  getLocalLiveEventStatusLabel,
  getLocalLiveStatusMessage,
  getLocalLiveHostedMakersForStop,
  getLocalLiveStopHoursText,
  getLocalLiveStopStatusLabel,
  sortLocalLiveStopsByEventOrder,
} from "../../utils/localLive";
import { FALL_CRAWL_PASSPORT_KEY } from "../../utils/fallCrawlPassport";
import {
  buildFallCrawlPublicRevealView,
  buildFallCrawlRevealEvent,
} from "../../utils/fallCrawlReveal";
import { trackFallCrawlInteraction } from "../Analytics/FallCrawlInteractionAnalytics";
import FallCrawlPassport from "./FallCrawlPassport";
import "./LocalLive.css";

const FALL_CRAWL_ASSET_BASE = "/assets/fall-crawl-2026/fall-crawl-2026";
const FALL_CRAWL_WEB_ASSETS = `${FALL_CRAWL_ASSET_BASE}/web`;
const FALL_CRAWL_MASTER_ASSETS = `${FALL_CRAWL_ASSET_BASE}/masters`;

const ASSETS = {
  wordmark: `${FALL_CRAWL_WEB_ASSETS}/fall-crawl-main-wordmark.webp`,
  compactBadge: `${FALL_CRAWL_WEB_ASSETS}/fall-crawl-compact-badge.webp`,
  fueledArtwork: `${FALL_CRAWL_WEB_ASSETS}/fall-crawl-fueled-by-mfp.webp`,
  mfpLogo: `${FALL_CRAWL_MASTER_ASSETS}/mfp-original-logo.png`,
  farmStand: `${FALL_CRAWL_WEB_ASSETS}/illustration-farm-stand.webp`,
  pumpkins: `${FALL_CRAWL_WEB_ASSETS}/illustration-pumpkins.webp`,
  apples: `${FALL_CRAWL_WEB_ASSETS}/illustration-apples.webp`,
  bread: `${FALL_CRAWL_WEB_ASSETS}/illustration-bread.webp`,
  flowers: `${FALL_CRAWL_WEB_ASSETS}/illustration-flowers.webp`,
  honey: `${FALL_CRAWL_WEB_ASSETS}/illustration-honey.webp`,
  overviewFlowers: `${FALL_CRAWL_ASSET_BASE}/individual/illustration-flowers-overview-clean.png`,
  overviewHoney: `${FALL_CRAWL_ASSET_BASE}/individual/illustration-honey-overview-clean.png`,
  overviewRibbon: `${FALL_CRAWL_WEB_ASSETS}/section-event-overview.webp`,
  stopsRibbon: `${FALL_CRAWL_WEB_ASSETS}/section-participating-stops.webp`,
  routeRibbon: `${FALL_CRAWL_WEB_ASSETS}/section-the-route.webp`,
  planRibbon: `${FALL_CRAWL_ASSET_BASE}/individual/section-plan-your-visit-clean.png`,
  routePath: `${FALL_CRAWL_WEB_ASSETS}/route-dotted-path.webp`,
  farmCategory: `${FALL_CRAWL_WEB_ASSETS}/category-farm-stand.webp`,
  makerCategory: `${FALL_CRAWL_WEB_ASSETS}/category-maker-market.webp`,
  eatsCategory: `${FALL_CRAWL_WEB_ASSETS}/category-eats-treats.webp`,
  hostBadge: `${FALL_CRAWL_WEB_ASSETS}/badge-host-location.webp`,
  makerBadge: `${FALL_CRAWL_WEB_ASSETS}/badge-maker.webp`,
  liveBadge: `${FALL_CRAWL_WEB_ASSETS}/badge-live.webp`,
  newStopBadge: `${FALL_CRAWL_WEB_ASSETS}/badge-new-stop.webp`,
  acornDivider: `${FALL_CRAWL_WEB_ASSETS}/ornament-acorn-leaf-divider.webp`,
  curlingVine: `${FALL_CRAWL_WEB_ASSETS}/ornament-curling-vine.webp`,
  dottedDivider: `${FALL_CRAWL_WEB_ASSETS}/ornament-dotted-divider.webp`,
  mapleCorner: `${FALL_CRAWL_WEB_ASSETS}/ornament-maple-corner.webp`,
  oakCorner: `${FALL_CRAWL_WEB_ASSETS}/ornament-oak-corner.webp`,
  singleAcorn: `${FALL_CRAWL_WEB_ASSETS}/ornament-single-acorn.webp`,
  pinHeart: `${FALL_CRAWL_WEB_ASSETS}/pin-heart.webp`,
  pinHost: `${FALL_CRAWL_WEB_ASSETS}/pin-host.webp`,
  pinShopping: `${FALL_CRAWL_WEB_ASSETS}/pin-shopping.webp`,
  pinStar: `${FALL_CRAWL_WEB_ASSETS}/pin-star.webp`,
};

const FALL_CRAWL_TICK_MS = 1000;
const FALL_CRAWL_COMPLETED_HEADING = "THANKS FOR CRAWLING WITH US!";
const FALL_CRAWL_COMPLETED_BODY = "The Fall Crawl 2026 has wrapped, but we’re leaving the route up for a few days so you can revisit participating stops, find places you missed, and keep supporting local.";

const formatFallCrawlDate = (dateValue) => {
  if (!dateValue) return "Date to be announced";
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatFallCrawlDateRange = (event = {}) => {
  const start = formatFallCrawlDate(event.startDate);
  const end = event.endDate && event.endDate !== event.startDate
    ? formatFallCrawlDate(event.endDate)
    : "";
  return end ? `${start} - ${end}` : start;
};

const formatFallCrawlTime = (value) => {
  if (!value) return "";
  const [hourValue, minuteValue = "00"] = String(value).split(":");
  const hour = Number(hourValue);
  if (!Number.isFinite(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minuteValue.padStart(2, "0")} ${suffix}`;
};

const getStopLocation = (stop = {}) =>
  [stop.city, stop.state].filter(Boolean).join(", ") ||
  stop.publicLocationLabel ||
  "Location details coming soon";

const getOfficialUpdate = (updates = []) =>
  updates.find((update) => update.official === true || update.authorType === "organizer") || null;

const getLatestParticipantUpdate = (updates = [], stopId) =>
  updates.find((update) => update.participantId === stopId && update.official !== true) || null;

const getCommunities = (stops = []) => {
  const names = stops
    .map((stop) => [stop.city, stop.state].filter(Boolean).join(", "))
    .filter(Boolean);
  return [...new Set(names)];
};

const getStopPhotoUrl = (stop = {}) => {
  if (stop.photoUrl) return stop.photoUrl;
  if (stop.imageUrl) return stop.imageUrl;
  if (stop.bannerUrl) return stop.bannerUrl;
  if (Array.isArray(stop.photos) && stop.photos[0]) return stop.photos[0];
  if (Array.isArray(stop.photoUrls) && stop.photoUrls[0]) return stop.photoUrls[0];
  if (Array.isArray(stop.images) && stop.images[0]) {
    return typeof stop.images[0] === "string" ? stop.images[0] : stop.images[0].url;
  }
  return "";
};

const hideBrokenImage = (event) => {
  event.currentTarget.style.display = "none";
};

const CountdownValue = ({ label, value }) => (
  <span className="fall-crawl-countdown-value">
    <strong>{String(value).padStart(2, "0")}</strong>
    <span>{label}</span>
  </span>
);

const FallCrawlCountdown = ({ event, onPhaseChange }) => {
  const [timeline, setTimeline] = useState(() => getFallCrawlEventTimeline(event));

  useEffect(() => {
    const updateTimeline = () => {
      const nextTimeline = getFallCrawlEventTimeline(event);
      setTimeline(nextTimeline);
      onPhaseChange?.(nextTimeline.phase);
    };

    updateTimeline();
    const timerId = window.setInterval(updateTimeline, FALL_CRAWL_TICK_MS);
    return () => window.clearInterval(timerId);
  }, [event, onPhaseChange]);

  if (timeline.phase === "upcoming") {
    const parts = getFallCrawlCountdownParts(timeline.msToStart);
    return (
      <section className="fall-crawl-countdown" aria-labelledby="fall-crawl-countdown-title">
        <p id="fall-crawl-countdown-title">THE FALL CRAWL BEGINS IN</p>
        <div className="fall-crawl-countdown-grid" aria-label={`${parts.days} days ${parts.hours} hours ${parts.minutes} minutes ${parts.seconds} seconds until The Fall Crawl begins`}>
          <CountdownValue label="Days" value={parts.days} />
          <CountdownValue label="Hours" value={parts.hours} />
          <CountdownValue label="Minutes" value={parts.minutes} />
          <CountdownValue label="Seconds" value={parts.seconds} />
        </div>
        <small>Countdown shown in {FALL_CRAWL_EVENT_TIME_ZONE}.</small>
      </section>
    );
  }

  if (timeline.phase === "live") {
    const parts = getFallCrawlCountdownParts(timeline.msToEnd);
    const timeRemaining = parts.days > 0
      ? `${parts.days} DAYS ${parts.hours} HOURS LEFT TO CRAWL`
      : `${parts.hours} HOURS ${parts.minutes} MINUTES LEFT TO CRAWL`;
    return (
      <section className="fall-crawl-countdown is-live" aria-labelledby="fall-crawl-live-title">
        <p id="fall-crawl-live-title">THE FALL CRAWL IS LIVE</p>
        <strong>{timeRemaining}</strong>
      </section>
    );
  }

  if (timeline.phase === "completed") {
    return (
      <section className="fall-crawl-countdown is-complete" aria-labelledby="fall-crawl-complete-title">
        <p id="fall-crawl-complete-title">{FALL_CRAWL_COMPLETED_HEADING}</p>
        <span>{FALL_CRAWL_COMPLETED_BODY}</span>
      </section>
    );
  }

  if (timeline.phase === "paused") {
    return (
      <section className="fall-crawl-countdown is-paused" aria-labelledby="fall-crawl-paused-title">
        <p id="fall-crawl-paused-title">THE FALL CRAWL IS PAUSED</p>
        <span>Check the live map for organizer updates before heading out.</span>
      </section>
    );
  }

  return (
    <section className="fall-crawl-countdown is-unavailable" aria-labelledby="fall-crawl-schedule-title">
      <p id="fall-crawl-schedule-title">EVENT SCHEDULE COMING SOON</p>
      <span>The official event timing will appear here once it is configured.</span>
    </section>
  );
};

const FallCrawlHomepage = ({
  event,
  stops = [],
  vendors = [],
  updates = [],
  preview = false,
  publicRevealPreview = false,
  fallCrawlSitePublished = false,
  revealOverride = null,
}) => {
  const eventForReveal = useMemo(() => (
    preview
      ? { ...(event || {}), passportEventKey: event?.passportEventKey || FALL_CRAWL_PASSPORT_KEY }
      : event
  ), [event, preview]);
  const revealView = useMemo(() => buildFallCrawlPublicRevealView({
    event: eventForReveal,
    stops,
    vendors,
    updates,
    override: revealOverride,
    adminFullPreview: preview && !publicRevealPreview,
  }), [eventForReveal, preview, publicRevealPreview, revealOverride, stops, updates, vendors]);
  const orderedStops = useMemo(() => sortLocalLiveStopsByEventOrder(revealView.stops), [revealView.stops]);
  const passportEvent = useMemo(() => (
    preview && !publicRevealPreview
      ? eventForReveal
      : buildFallCrawlRevealEvent(eventForReveal, revealOverride)
  ), [eventForReveal, preview, publicRevealPreview, revealOverride]);
  const featuredStopsMode = revealView.reveal.stopsRevealMode === "featured";
  const communities = useMemo(() => getCommunities(orderedStops), [orderedStops]);
  const officialUpdate = useMemo(() => getOfficialUpdate(revealView.updates), [revealView.updates]);
  const scheduleEvent = useMemo(
    () => getFallCrawlScheduleEvent(event || {}, stops),
    [event, stops]
  );
  const [lifecyclePhase, setLifecyclePhase] = useState(() => getFallCrawlEventTimeline(scheduleEvent || {}).phase);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("fall-crawl-page-active");
    return () => {
      document.body.classList.remove("fall-crawl-page-active");
    };
  }, []);

  useEffect(() => {
    setLifecyclePhase(getFallCrawlEventTimeline(scheduleEvent || {}).phase);
  }, [scheduleEvent]);

  const updateLifecyclePhase = useCallback((nextPhase) => {
    setLifecyclePhase((currentPhase) => currentPhase === nextPhase ? currentPhase : nextPhase);
  }, []);

  if (!event) return null;

  const isLiveNow = lifecyclePhase === "live";
  const isCompleted = lifecyclePhase === "completed";
  const exploreClassName = `fall-crawl-button ${isLiveNow ? "secondary" : "primary"}`;
  const liveMapClassName = `fall-crawl-button ${isLiveNow ? "primary is-live" : "secondary"}`;
  const localLivePath = (preview || publicRevealPreview) && event.id
    ? `/admin/local-live/${event.id}/map-preview${publicRevealPreview ? "?publicRevealPreview=1" : ""}`
    : event.slug
      ? `/local-live/${event.slug}`
      : "/events";
  const mapCtaText = isLiveNow ? "LIVE - View Live Map" : isCompleted ? "View Map" : "View Live Map";
  const phaseCopy = isCompleted ? FALL_CRAWL_COMPLETED_BODY : getLocalLiveStatusMessage(scheduleEvent);
  const isPublicFallCrawlAnalyticsEvent = !preview && !publicRevealPreview && (
    event.passportEventKey === FALL_CRAWL_PASSPORT_KEY ||
    event.slug === FALL_CRAWL_PASSPORT_KEY
  );
  const trackDirectionsClick = (stop) => {
    if (!isPublicFallCrawlAnalyticsEvent || !event.id) return;
    trackFallCrawlInteraction("directions_click", {
      eventId: event.id,
      stopId: stop.id,
      stopName: stop.name || "",
      allowRepeat: true,
    });
  };

  return (
    <main className={`fall-crawl-homepage ${preview ? "is-preview" : ""}`}>
      {(preview || publicRevealPreview) && (
        <div className="fall-crawl-preview-banner" role="status">
          {publicRevealPreview
            ? fallCrawlSitePublished
              ? "PUBLIC REVEAL PREVIEW - SITE CURRENTLY PUBLIC"
              : "PUBLIC REVEAL PREVIEW · SITE CURRENTLY LOCKED"
            : "PREVIEW - NOT PUBLIC"}
        </div>
      )}
      <header className="fall-crawl-site-header" aria-label="Fall Crawl site header">
        <Link className="fall-crawl-site-brand" to="/fall-crawl/2026">
          <img src={ASSETS.compactBadge} alt="The Fall Crawl 2026" width="180" height="180" />
          <span>
            <strong>The Fall Crawl</strong>
            <small>Fueled by MyFarmingPal</small>
          </span>
        </Link>
        <nav className="fall-crawl-site-nav" aria-label="Fall Crawl navigation">
          <a href="#fall-crawl-stops">Stops</a>
          {revealView.showMap ? <Link to={localLivePath}>{isCompleted ? "Map" : "Live Map"}</Link> : <a href="#fall-crawl-map">{isCompleted ? "Map" : "Live Map"}</a>}
          <Link to="/home">Full MFP Site</Link>
        </nav>
      </header>
      <section className="fall-crawl-hero" aria-labelledby="fall-crawl-title">
        <div className="fall-crawl-hero-copy">
          <p className="fall-crawl-eyebrow">Local Farm Stand &amp; Maker Event</p>
          <img
            className="fall-crawl-wordmark"
            src={ASSETS.wordmark}
            alt="The Fall Crawl 2026"
            width="760"
            height="291"
          />
          <h1 id="fall-crawl-title" className="fall-crawl-sr-title">
            The Fall Crawl 2026
          </h1>
          <p className="fall-crawl-fueled">Fueled by MyFarmingPal</p>
          <div className="fall-crawl-event-meta" aria-label="Event date and time">
            <div>
              <span>Date</span>
              <strong>{formatFallCrawlDateRange(scheduleEvent)}</strong>
            </div>
            <div>
              <span>Hours</span>
              <strong>{getFallCrawlEventHoursText(event, stops)}</strong>
            </div>
            <div className="fall-crawl-event-meta-attendee">
              <span>Attendee Info</span>
              <strong>FREE EVENT &bull; BEDFORD &amp; COFFEE COUNTIES, TN &bull; DRIVE-YOUR-OWN CRAWL</strong>
            </div>
          </div>
          <FallCrawlCountdown event={scheduleEvent} onPhaseChange={updateLifecyclePhase} />
          <p className="fall-crawl-hero-lede">
            A regional day of farm stands, makers, small businesses, and backroad exploring.
            Pick your starting point, watch the reveals, and support local at your own pace.
          </p>
          {revealView.publicStatusLine && !isCompleted && <p className="fall-crawl-reveal-status">{revealView.publicStatusLine}</p>}
          <p className="fall-crawl-phase-copy">{phaseCopy}</p>
          <div className="fall-crawl-hero-actions">
            <a className={exploreClassName} href="#fall-crawl-stops">
              Explore The Crawl
            </a>
            {revealView.showMap ? (
              <Link className={liveMapClassName} to={localLivePath}>
                {mapCtaText}
              </Link>
            ) : (
              <a className={liveMapClassName} href="#fall-crawl-map">
                Map Coming Soon
              </a>
            )}
          </div>
          <p className={`fall-crawl-status status-${event.status || "published"}`}>
            {getLocalLiveEventStatusLabel(scheduleEvent)}
          </p>
        </div>
        <div className="fall-crawl-hero-art" aria-hidden="true">
          <img className="fall-crawl-hero-vine" src={ASSETS.curlingVine} alt="" width="260" height="260" />
          <img className="fall-crawl-hero-leaves" src={ASSETS.mapleCorner} alt="" width="180" height="180" />
          <img className="fall-crawl-farm-stand-art" src={ASSETS.farmStand} alt="" width="450" height="450" />
          <img className="fall-crawl-pumpkin-art" src={ASSETS.pumpkins} alt="" width="220" height="220" />
          <img className="fall-crawl-hero-honey-art" src={ASSETS.honey} alt="" width="140" height="140" />
        </div>
      </section>

      <section className="fall-crawl-overview" aria-labelledby="fall-crawl-overview-title">
        <div className="fall-crawl-overview-layout">
          <div className="fall-crawl-section-heading">
            <img src={ASSETS.overviewRibbon} alt="" loading="lazy" width="320" height="91" />
            <p className="fall-crawl-eyebrow">A town-and-country market day</p>
            <h2 id="fall-crawl-overview-title">Spend the day moving from stop to stop.</h2>
            <p>
              The Fall Crawl brings together local farm stands, hosted makers, seasonal goods,
              and small businesses in one easy-to-follow Local Live experience.
            </p>
            {event.description && <p>{event.description}</p>}
          </div>
          <div className="fall-crawl-overview-art" aria-hidden="true">
            <img src={ASSETS.overviewFlowers} alt="" loading="lazy" width="180" height="180" />
            <img src={ASSETS.bread} alt="" loading="lazy" width="180" height="180" />
            <img src={ASSETS.overviewHoney} alt="" loading="lazy" width="160" height="160" />
            <img src={ASSETS.apples} alt="" loading="lazy" width="160" height="160" />
          </div>
        </div>
        <div className="fall-crawl-overview-grid">
          <article className="fall-crawl-category-callout">
            <img src={ASSETS.farmCategory} alt="" loading="lazy" width="120" height="120" />
            <div>
              <h3>Farm stands</h3>
              <p>Visit host destinations, see what is fresh, and use the live map for route order and directions.</p>
            </div>
          </article>
          <article className="fall-crawl-category-callout">
            <img src={ASSETS.makerCategory} alt="" loading="lazy" width="120" height="120" />
            <div>
              <h3>Makers</h3>
              <p>Find local makers and pop-ups organized underneath the stops hosting them for the day.</p>
            </div>
          </article>
          <article className="fall-crawl-category-callout">
            <img src={ASSETS.eatsCategory} alt="" loading="lazy" width="120" height="120" />
            <div>
              <h3>Local treats</h3>
              <p>Make room for seasonal finds, baked goods, honey, flowers, and other stop-specific offerings.</p>
            </div>
          </article>
        </div>
      </section>

      <section className="fall-crawl-how" aria-labelledby="fall-crawl-how-title">
        <div className="fall-crawl-section-heading">
          <img className="fall-crawl-plan-ribbon" src={ASSETS.planRibbon} alt="" loading="lazy" width="320" height="91" />
          <p className="fall-crawl-eyebrow">How the crawl works</p>
          <h2 id="fall-crawl-how-title">{revealView.showRouteLanguage ? "Start anywhere, then follow the recommended route." : "Start anywhere, then follow each reveal."}</h2>
        </div>
        <div className="fall-crawl-journey">
          <ol className="fall-crawl-steps">
          {(revealView.showRouteLanguage ? [
            ["1", "Pick a starting point", "Choose the stop that works best for your morning."],
            ["2", "Follow the route", "Use the numbered order as the suggested flow between stops."],
            ["3", "Explore at your pace", "Stay longer where you want and skip ahead when you need."],
            ["4", "Support local", "Shop, meet makers, and keep the day moving through the region."],
          ] : [
            ["1", "Save the date", "The public countdown follows the real event schedule."],
            ["2", "Watch for confirmed stops", "A few destinations can be revealed before the full roster is ready."],
            ["3", "Open the book", "The Backroads Book may unlock before stamp collection goes live."],
            ["4", "Support local", "More details will appear here as the organizer reveals them."],
          ]).map(([number, title, copy]) => (
            <li key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </li>
          ))}
          </ol>
        </div>
      </section>

      <section className="fall-crawl-stops" id="fall-crawl-stops" aria-labelledby="fall-crawl-stops-title">
        <img className="fall-crawl-section-ornament top-right" src={ASSETS.mapleCorner} alt="" loading="lazy" width="180" height="180" />
        <div className="fall-crawl-section-heading">
          <img src={ASSETS.stopsRibbon} alt="" loading="lazy" width="320" height="91" />
          <p className="fall-crawl-eyebrow">{featuredStopsMode ? "A few confirmed stops" : "Participating stops"}</p>
          <h2 id="fall-crawl-stops-title">{featuredStopsMode ? "Stops already joining the Crawl" : "Official crawl destinations"}</h2>
          <p>{featuredStopsMode ? "More stops are being revealed as the Crawl gets closer." : orderedStops.length > 0 ? `${orderedStops.length} ${orderedStops.length === 1 ? "stop is" : "stops are"} currently revealed.` : "Confirmed stops will appear as they are revealed."}</p>
        </div>
        {orderedStops.length === 0 ? (
          <p className="fall-crawl-empty">Participating stops will appear here as they are confirmed.</p>
        ) : (
          <div className="fall-crawl-stop-grid">
            {orderedStops.map((stop) => {
              const hostedMakers = getLocalLiveHostedMakersForStop(revealView.vendors, stop.id);
              const latestUpdate = getLatestParticipantUpdate(revealView.updates, stop.id);
              const directionsUrl = getLocalLiveDirectionsUrl(stop);
              const stopPhotoUrl = getStopPhotoUrl(stop);
              const stopStatus = getLocalLiveEffectiveStopStatus(stop, scheduleEvent);
              const stopHours = getLocalLiveStopHoursText(stop, scheduleEvent);
              return (
                <article className={`fall-crawl-stop-card ${hostedMakers.length > 0 ? "has-hosted-makers" : ""}`} key={stop.id}>
                  <div className="fall-crawl-stop-media">
                    {stopPhotoUrl && (
                      <img
                        src={stopPhotoUrl}
                        alt={`${stop.name || "Local stop"} stop`}
                        loading="lazy"
                        onError={hideBrokenImage}
                      />
                    )}
                    <div className="fall-crawl-stop-fallback" aria-hidden="true">
                      <img src={ASSETS.pinStar} alt="" loading="lazy" width="96" height="96" />
                      <span>Fall Crawl Stop</span>
                    </div>
                  </div>
                  <div className="fall-crawl-stop-content">
                    <div className="fall-crawl-stop-topline">
                      <p className="fall-crawl-stop-location">{getStopLocation(stop)}</p>
                      {hostedMakers.length > 0 && (
                        <span className="fall-crawl-host-badge">
                          <img src={ASSETS.hostBadge} alt="" loading="lazy" width="84" height="84" />
                          Host Location
                        </span>
                      )}
                    </div>
                    <h3>{stop.name || "Local stop"}</h3>
                    <p className="fall-crawl-stop-hours">
                      <span className={`fall-crawl-stop-status status-${stopStatus}`}>{getLocalLiveStopStatusLabel(stopStatus)}</span>
                      {stopHours && <><strong>Hours:</strong> {stopHours}</>}
                    </p>
                    {stop.offeringsSummary && <p>{stop.offeringsSummary}</p>}
                    {stop.eventNote && <p>{stop.eventNote}</p>}
                    {latestUpdate?.text && (
                      <p className="fall-crawl-live-note">
                        <strong>Latest update:</strong> {latestUpdate.text}
                      </p>
                    )}
                    {hostedMakers.length > 0 && (
                      <div className="fall-crawl-makers-list">
                        <strong><img src={ASSETS.makerBadge} alt="" loading="lazy" width="42" height="42" /> Hosted makers</strong>
                        {hostedMakers.map((vendor) => (
                          <span key={vendor.id}>
                            {vendor.name}{vendor.summary ? ` - ${vendor.summary}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="fall-crawl-stop-actions">
                      {revealView.showMap && <Link className="fall-crawl-text-link" to={localLivePath}>{isCompleted ? "Open in Map" : "Open in Live Map"}</Link>}
                      {directionsUrl && (
                        <a
                          className="fall-crawl-text-link"
                          href={directionsUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => trackDirectionsClick(stop)}
                        >
                          Directions
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="fall-crawl-route-cta" id={revealView.showMap ? "fall-crawl-map" : "fall-crawl-route"} aria-labelledby="fall-crawl-route-title">
        <div>
          <img src={ASSETS.routeRibbon} alt="" loading="lazy" width="320" height="91" />
          <p className="fall-crawl-eyebrow">{isCompleted ? "The route and map" : "The route and live map"}</p>
          <h2 id="fall-crawl-route-title">{isCompleted ? "Revisit the route when you are ready." : "Use Local Live when you are ready to go."}</h2>
          <p>{revealView.showMap ? isCompleted ? "The map remains available so visitors can revisit participating stops and route details after the event." : "The live map is staged from the public reveal settings, so visitors only see the stops and route details currently announced." : "The Fall Crawl map is coming soon. Confirmed details will appear here as the organizer reveals them."}</p>
          <ul className="fall-crawl-route-benefits">
            <li>{revealView.reveal.stopsRevealMode === "featured" ? "See featured confirmed stops" : "See participating stops"}</li>
            <li>{revealView.showRouteLanguage ? "Follow the route" : "Watch for the route reveal"}</li>
            <li>{isCompleted ? "Review event updates" : "View live updates"}</li>
            <li>Get directions</li>
            <li>See your location when permission is granted</li>
          </ul>
          {officialUpdate && (
            <p className="fall-crawl-official-update">
              <strong>Official update:</strong> {officialUpdate.text}
              {officialUpdate.createdAt && <span>{formatLocalLiveUpdatedAt(officialUpdate.createdAt)}</span>}
            </p>
          )}
          {revealView.showMap ? (
            <Link className={liveMapClassName} to={localLivePath}>
              {isLiveNow ? "LIVE - Open Live Map" : isCompleted ? "Open Map" : "Open Live Map"}
            </Link>
          ) : (
            <div className="fall-crawl-map-teaser" id="fall-crawl-map">
              <strong>THE FALL CRAWL MAP</strong>
              <span>Coming soon</span>
            </div>
          )}
        </div>
        <div className="fall-crawl-route-art" aria-hidden="true">
          <img className={`fall-crawl-route-line-art ${revealView.showRouteLanguage ? "" : "is-hidden"}`} src={ASSETS.routePath} alt="" loading="lazy" width="760" height="168" />
          <img className="fall-crawl-route-seal" src={ASSETS.compactBadge} alt="" loading="lazy" width="180" height="180" />
          <img src={ASSETS.liveBadge} alt="" loading="lazy" width="170" height="170" />
          <img src={ASSETS.apples} alt="" loading="lazy" width="170" height="170" />
          <img src={ASSETS.honey} alt="" loading="lazy" width="170" height="170" />
          <img src={ASSETS.mapleCorner} alt="" loading="lazy" width="170" height="170" />
        </div>
      </section>

      {revealView.showPassport && (
        <div id="backroads-book">
          <FallCrawlPassport
            event={passportEvent}
            stops={revealView.passportStops}
            preview={preview && !publicRevealPreview}
            revealMode={revealView.passportRevealMode}
            collectionEnabled={revealView.passportCollectionEnabled}
            mysteryMode={revealView.passportMysteryMode}
          />
        </div>
      )}

      <section className={`fall-crawl-communities ${communities.length > 0 ? "has-communities" : "is-empty"}`} aria-labelledby="fall-crawl-communities-title">
        {communities.length > 0 && <img className="fall-crawl-community-road" src={ASSETS.routePath} alt="" loading="lazy" width="760" height="168" />}
        <div className="fall-crawl-section-heading">
          <p className="fall-crawl-eyebrow">Communities</p>
          <h2 id="fall-crawl-communities-title">One day. Multiple communities.</h2>
          <p>{revealView.showRouteLanguage ? "Communities below are derived from the currently configured participating stops." : "Communities will continue appearing as public reveals are announced."}</p>
        </div>
        {communities.length > 0 ? (
          <>
            <p className="fall-crawl-community-count">{communities.length}</p>
            <div className="fall-crawl-community-list">
              {communities.map((community, index) => (
                <span key={community}>
                  <img src={ASSETS.pinStar} alt="" loading="lazy" width="48" height="48" />
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  {community}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="fall-crawl-empty">Communities will appear as participating stops are confirmed.</p>
        )}
      </section>

      <section className="fall-crawl-mfp" aria-labelledby="fall-crawl-mfp-title">
        <img className="fall-crawl-section-divider" src={ASSETS.dottedDivider} alt="" loading="lazy" width="420" height="120" />
        <div>
          <p className="fall-crawl-eyebrow">Fueled by MyFarmingPal</p>
          <h2 id="fall-crawl-mfp-title">The Crawl is one day. Local community is year-round.</h2>
          <p>
            MyFarmingPal powers the live route for The Fall Crawl and keeps the local discovery going
            afterward with farm stands, Live Shelf, marketplace browsing, events, jobs, Land Share, and more.
          </p>
          <Link className="fall-crawl-button primary" to="/marketplace">
            Explore MyFarmingPal
          </Link>
        </div>
        <div className="fall-crawl-mfp-mark">
          <img className="fall-crawl-mfp-fueled" src={ASSETS.fueledArtwork} alt="" loading="lazy" width="320" height="180" />
          <img className="fall-crawl-mfp-logo" src={ASSETS.mfpLogo} alt="MyFarmingPal" width="420" height="145" />
          <img className="fall-crawl-mfp-acorn" src={ASSETS.singleAcorn} alt="" loading="lazy" width="110" height="110" />
        </div>
      </section>

      <footer className="fall-crawl-footer">
        <img className="fall-crawl-footer-corner left" src={ASSETS.oakCorner} alt="" loading="lazy" width="180" height="180" />
        <img className="fall-crawl-footer-corner right" src={ASSETS.mapleCorner} alt="" loading="lazy" width="180" height="180" />
        <img src={ASSETS.acornDivider} alt="" loading="lazy" width="420" height="120" />
        <h2>{isCompleted ? "Keep supporting local." : "Keep supporting local."}</h2>
        <p>{isCompleted ? "Revisit the participating stops, communities, and makers from The Fall Crawl." : "Explore the route, meet the people behind the stops, and support local this fall."}</p>
        {revealView.showMap ? (
          <Link className="fall-crawl-button secondary" to={localLivePath}>
            {isLiveNow ? "Open Live Map" : isCompleted ? "Open Map" : "Explore the Stops"}
          </Link>
        ) : (
          <a className="fall-crawl-button secondary" href="#fall-crawl-map">Map Coming Soon</a>
        )}
        <small>Powered by MyFarmingPal</small>
      </footer>
    </main>
  );
};

export default FallCrawlHomepage;
